import { Component, Input, OnChanges, SimpleChanges, computed, inject, signal } from '@angular/core';
import { ModalReturnData, ModalService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { catchError, finalize, of, switchMap } from 'rxjs';
import {
  AP_DOCUMENT_ENTITY_DEFINITION_CODE,
  TaxAuthorization,
  authorizationCoverageLabel,
} from '../../../models/tax-authorization/tax-authorization';
import { TaxAuthorizationProxyService } from '../../../proxies/tax-authorization.proxy.service';
import { SiteBusinessService } from '../../../services/site-business.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TaxAuthorizationDetailsModalComponent } from '../../tax-authorizations/tax-authorization-details-modal/tax-authorization-details-modal.component';

/**
 * A counterparty's own fiscal authorizations, shown as a tab on Business Details (#4791).
 *
 * <p>These are INCOMING authorizations: the CAI a provider prints on their invoices. Recording one here is
 * what lets AP validate that a provider invoice number falls inside a range the tax authority actually
 * granted them — see ValidateProviderCaiAsync in Finance.API.</p>
 *
 * <p>An authorization is granted per document type, so each row records which AP document it covers —
 * Invoice, Receipt, Debit Note or Credit Note, the kinds a provider issues to us. Purchase Receipt and
 * Retención we self-issue against our own range, and a Policy carries no CAI at all (#6016), so a provider
 * never holds one for those.</p>
 *
 * <p>The tab refuses to manage the site's OWN company: those ranges ISSUE numbers rather than validate
 * them, span several entity definitions, and belong on the Tax Authorizations page — so it points there
 * instead of offering an edit the API would reject.</p>
 */
@Component({
  selector: 'app-tax-authorizations-viewer',
  templateUrl: './tax-authorizations-viewer.component.html',
})
export class TaxAuthorizationsViewerComponent implements OnChanges {
  /** The business on screen. Null while creating one — it has no id to attach an authorization to yet. */
  @Input() businessId: string | null = null;
  @Input() businessName: string | null = null;
  @Input() viewOnly = true;

  private readonly proxy = inject(TaxAuthorizationProxyService);
  private readonly siteBusinessService = inject(SiteBusinessService);
  private readonly modalService = inject(ModalService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(false);
  records = signal<TaxAuthorization[]>([]);
  /** True when the business on screen is the company this site operates under. */
  isSiteBusiness = signal(false);

  readonly isNewBusiness = computed(() => !this.businessId);
  readonly canManage = computed(() => !this.viewOnly && !this.isSiteBusiness() && !this.isNewBusiness());

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) {
      this.load();
    }
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private load(): void {
    this.records.set([]);
    this.isSiteBusiness.set(false);

    const businessId = this.businessId;
    if (!businessId) {
      return;
    }

    this.isLoading.set(true);
    this.siteBusinessService
      .isSiteBusiness(businessId)
      .pipe(
        switchMap((isOwn) => {
          this.isSiteBusiness.set(isOwn);
          // Our own company's ranges are the other kind entirely; listing them here would invite edits the
          // API rejects, so the tab shows the pointer to the Tax Authorizations page and nothing else.
          if (isOwn) {
            return of({ success: true, data: [] as TaxAuthorization[], errorMessage: null });
          }
          return this.proxy.search(businessId, undefined, AP_DOCUMENT_ENTITY_DEFINITION_CODE);
        }),
        catchError(() => of({ success: false, data: [] as TaxAuthorization[], errorMessage: null })),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((response) => {
        this.records.set(response.success && response.data ? response.data : []);
      });
  }

  // ── Create / edit ───────────────────────────────────────────────────────────
  openCreate(): void {
    this.openModal(null);
  }

  openEdit(record: TaxAuthorization): void {
    if (record.isSystem || !this.canManage()) {
      return;
    }
    this.openModal(record);
  }

  private openModal(authorization: TaxAuthorization | null): void {
    if (!this.canManage()) {
      return;
    }

    this.modalService
      .open(
        TaxAuthorizationDetailsModalComponent,
        {},
        { authorization, mode: 'provider', businessId: this.businessId, businessName: this.businessName },
      )
      .subscribe((result: ModalReturnData<TaxAuthorization | null>) => {
        if (result.action === 'Save') {
          this.load();
        }
      });
  }

  // ── Deactivate ──────────────────────────────────────────────────────────────
  confirmDeactivate(record: TaxAuthorization): void {
    this.confirmationService.confirm({
      // Keyed to this tab's own dialog so it cannot be picked up by another host on the page.
      key: 'tax-authorizations-viewer',
      header: this.translate('Cai_Deactivate_Title', 'Deactivate authorization'),
      message: this.translate(
        'Cai_Deactivate_Message_Provider',
        'Deactivate authorization {cai}? Invoices quoting it will no longer validate.',
      ).replace('{cai}', record.authorizationNumber),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deactivate(record),
    });
  }

  private deactivate(record: TaxAuthorization): void {
    this.proxy
      .deactivate(record.id)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        if (response === null || !response.success) {
          return;
        }
        GlobalMessageUtil.success('Tax Authorizations', 'Tax authorization deactivated.');
        this.load();
      });
  }

  /** Which AP documents the provider's CAI covers — one authorization can name several. */
  coverageLabel(documentTypes: number[] | null | undefined): string {
    return authorizationCoverageLabel(documentTypes);
  }

  trackByRecordId = (_: number, record: TaxAuthorization) => record.id;

  getRowData(rowData: unknown): TaxAuthorization {
    return rowData as TaxAuthorization;
  }
}
